import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { X, Eye, Code, Plus, Mail, FileText, AlertCircle, ChevronLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Template, templateVariables, TemplateVariable } from '@/types/template';
import { PDFDesignInterface } from '@/components/settings/pdf-design-interface';

export function TemplateEditorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get template and mode from location state or URL params
  const template = location.state?.template as Template | null;
  const mode = (searchParams.get('mode') || location.state?.mode || 'create') as 'create' | 'edit';

  const [formData, setFormData] = useState({
    name: '',
    type: 'email' as 'email' | 'statement',
    outputType: 'email' as 'email' | 'pdf',
    subject: '',
    content: '',
    isDefault: false,
    category: [] as string[],
  });
  const [pdfSettings, setPdfSettings] = useState<Template['pdfSettings']>(undefined);
  const [isPreview, setIsPreview] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [showOutputTypeWarning, setShowOutputTypeWarning] = useState(false);
  const [pendingOutputType, setPendingOutputType] = useState<'email' | 'pdf' | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Initialize form data when template changes
  useEffect(() => {
    if (template && mode === 'edit') {
      setFormData({
        name: template.name,
        type: template.type,
        outputType: template.outputType || 'email',
        subject: template.subject || '',
        content: template.content,
        isDefault: template.isDefault,
        category: template.category || [],
      });
      setPdfSettings(template.pdfSettings);
    } else {
      setFormData({
        name: '',
        type: 'email',
        outputType: 'email',
        subject: '',
        content: '',
        isDefault: false,
        category: [],
      });
      setPdfSettings(undefined);
    }
    setIsPreview(false);
    setShowOutputTypeWarning(false);
    setPendingOutputType(null);
  }, [template, mode]);

  // Group variables by category
  const groupedVariables = useMemo(() => {
    return templateVariables.reduce((acc, variable) => {
      if (!acc[variable.category]) {
        acc[variable.category] = [];
      }
      acc[variable.category].push(variable);
      return acc;
    }, {} as Record<string, TemplateVariable[]>);
  }, []);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (mode === 'create') {
      return formData.name !== '' || formData.content !== '' || formData.subject !== '';
    }
    if (template) {
      return (
        formData.name !== template.name ||
        formData.type !== template.type ||
        formData.outputType !== (template.outputType || 'email') ||
        formData.subject !== (template.subject || '') ||
        formData.content !== template.content ||
        formData.isDefault !== template.isDefault ||
        JSON.stringify(formData.category) !== JSON.stringify(template.category || [])
      );
    }
    return false;
  };

  const handleOutputTypeChange = (newOutputType: 'email' | 'pdf') => {
    if (hasUnsavedChanges() && formData.outputType !== newOutputType) {
      setPendingOutputType(newOutputType);
      setShowOutputTypeWarning(true);
    } else {
      setFormData({ ...formData, outputType: newOutputType });
    }
  };

  const confirmOutputTypeChange = () => {
    if (pendingOutputType) {
      setFormData({ ...formData, outputType: pendingOutputType });
      setShowOutputTypeWarning(false);
      setPendingOutputType(null);
    }
  };

  const cancelOutputTypeChange = () => {
    setShowOutputTypeWarning(false);
    setPendingOutputType(null);
  };

  const handleInsertVariable = (variableKey: string) => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content;
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newContent = before + variableKey + after;
      
      setFormData({ ...formData, content: newContent });
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variableKey.length, start + variableKey.length);
      }, 0);
    }
  };

  const handleAddCategory = () => {
    if (newCategory && !formData.category.includes(newCategory)) {
      setFormData({
        ...formData,
        category: [...formData.category, newCategory],
      });
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (category: string) => {
    setFormData({
      ...formData,
      category: formData.category.filter(c => c !== category),
    });
  };

  const handleSave = () => {
    // Extract variables used in content
    const variableRegex = /\{\{[^}]+\}\}/g;
    const usedVariables = formData.content.match(variableRegex) || [];
    const uniqueVariables = Array.from(new Set(usedVariables));

    const savedTemplate: Template = {
      id: template?.id || Date.now().toString(),
      name: formData.name,
      type: formData.type,
      outputType: formData.outputType,
      subject: formData.outputType === 'email' && formData.type === 'email' ? formData.subject : undefined,
      content: formData.content,
      isDefault: formData.isDefault,
      category: formData.category,
      variables: uniqueVariables,
      lastModified: new Date().toISOString(),
      createdAt: template?.createdAt || new Date().toISOString(),
      createdBy: template?.createdBy || 'Current User',
      pdfSettings: formData.outputType === 'pdf' ? pdfSettings : undefined,
    };

    // Pass the saved template back to the templates page
    navigate('/settings/templates', { state: { savedTemplate, mode } });
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setPendingNavigation('/settings/templates');
      setShowUnsavedChangesDialog(true);
    } else {
      navigate('/settings/templates');
    }
  };

  const confirmNavigation = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    setShowUnsavedChangesDialog(false);
    setPendingNavigation(null);
  };

  const cancelNavigation = () => {
    setShowUnsavedChangesDialog(false);
    setPendingNavigation(null);
  };

  // Render preview with sample data
  const renderPreview = () => {
    let previewContent = formData.content;
    
    // Replace variables with example values
    templateVariables.forEach(variable => {
      const regex = new RegExp(variable.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      previewContent = previewContent.replace(regex, `<span class="bg-blue-100 text-blue-800 px-1 rounded">${variable.example}</span>`);
    });

    return (
      <div className="prose prose-sm max-w-none">
        {formData.outputType === 'email' && formData.type === 'email' && formData.subject && (
          <div className="mb-4 p-3 bg-muted rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Subject:</div>
            <div className="font-semibold" dangerouslySetInnerHTML={{ 
              __html: formData.subject.replace(/\{\{[^}]+\}\}/g, (match) => {
                const variable = templateVariables.find(v => v.key === match);
                return variable ? `<span class="bg-blue-100 text-blue-800 px-1 rounded">${variable.example}</span>` : match;
              })
            }} />
          </div>
        )}
        <div dangerouslySetInnerHTML={{ __html: previewContent }} />
      </div>
    );
  };

  const breadcrumbItems = [
    { label: 'Settings', href: '/settings/templates' },
    { label: 'Document Templates', href: '/settings/templates' },
    { label: mode === 'create' ? 'Create Template' : 'Edit Template' },
  ];

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden">
      {/* Header with Breadcrumb */}
      <div className="border-b bg-background px-8 py-4">
        <div className="flex items-center justify-between mb-3">
          <Breadcrumb items={breadcrumbItems} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {mode === 'create' ? 'Create Template' : `Edit Template: ${template?.name}`}
            </h1>
            <p className="text-muted-foreground mt-1">
              {mode === 'create' 
                ? 'Create a new document template with custom content and variables'
                : 'Modify the template content and settings'
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.name || !formData.content}>
              <Save className="h-4 w-4 mr-2" />
              {mode === 'create' ? 'Create Template' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden px-8 py-6">
        <div className="grid grid-cols-3 gap-6 h-full">
          {/* Left Panel - Template Editor */}
          <div className="col-span-2 flex flex-col space-y-4 overflow-hidden">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template-name">Template Name *</Label>
                  <Input
                    id="template-name"
                    placeholder="Enter template name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-type">Template Type *</Label>
                  <Select value={formData.type} onValueChange={(value: any) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger id="template-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="statement">Statement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Output Type Dropdown - Prominently placed */}
              <div className="space-y-2 bg-primary/5 p-4 rounded-lg border-2 border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Label htmlFor="output-type" className="text-base font-semibold">Output Type *</Label>
                  <Badge variant="outline" className="text-xs">
                    {formData.outputType === 'email' ? 'Email Format' : 'PDF Document'}
                  </Badge>
                </div>
                <Select value={formData.outputType} onValueChange={handleOutputTypeChange}>
                  <SelectTrigger id="output-type" className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>Email</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="pdf">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        <span>PDF</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  {formData.outputType === 'email' 
                    ? 'Configure email-specific settings including subject line and email content'
                    : 'Design a PDF document with logo, layout, and styling options'
                  }
                </p>
              </div>

              {/* Email-specific fields */}
              {formData.outputType === 'email' && formData.type === 'email' && (
                <div className="space-y-2">
                  <Label htmlFor="template-subject">Subject Line</Label>
                  <Input
                    id="template-subject"
                    placeholder="Enter email subject (use variables like {{customer.name}})"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Conditional Content Based on Output Type */}
            {formData.outputType === 'email' ? (
              // Email Editor
              <Tabs value={isPreview ? 'preview' : 'edit'} onValueChange={(v) => setIsPreview(v === 'preview')} className="flex-1 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between">
                  <TabsList>
                    <TabsTrigger value="edit">
                      <Code className="h-4 w-4 mr-2" />
                      Edit
                    </TabsTrigger>
                    <TabsTrigger value="preview">
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="edit" className="flex-1 mt-4 overflow-hidden">
                  <div className="h-full flex flex-col">
                    <Label htmlFor="template-content" className="mb-2">Template Content</Label>
                    <Textarea
                      id="template-content"
                      placeholder="Enter your template content here. Use variables like {{customer.name}} to insert dynamic data."
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="flex-1 font-mono text-sm resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Tip: Select a variable from the dropdown in the right panel to insert it at cursor position
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="preview" className="flex-1 mt-4 overflow-hidden">
                  <ScrollArea className="h-full border rounded-lg p-4 bg-muted/30">
                    {renderPreview()}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            ) : (
              // PDF Editor
              <div className="flex-1 flex flex-col overflow-hidden">
                <Alert className="mb-4">
                  <FileText className="h-4 w-4" />
                  <AlertDescription>
                    <strong>PDF Design Mode:</strong> Configure your PDF document layout, styling, and content in the right panel. 
                    Use the content editor below for the main document body.
                  </AlertDescription>
                </Alert>

                <Tabs value={isPreview ? 'preview' : 'edit'} onValueChange={(v) => setIsPreview(v === 'preview')} className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex items-center justify-between">
                    <TabsList>
                      <TabsTrigger value="edit">
                        <Code className="h-4 w-4 mr-2" />
                        Edit Content
                      </TabsTrigger>
                      <TabsTrigger value="preview">
                        <Eye className="h-4 w-4 mr-2" />
                        Preview PDF
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="edit" className="flex-1 mt-4 overflow-hidden">
                    <div className="h-full flex flex-col">
                      <Label htmlFor="template-content" className="mb-2">PDF Content</Label>
                      <Textarea
                        id="template-content"
                        placeholder="Enter your PDF content here. Use variables like {{customer.name}} to insert dynamic data."
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        className="flex-1 font-mono text-sm resize-none"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Configure PDF layout, logo, and styling options in the right panel
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="preview" className="flex-1 mt-4 overflow-hidden">
                    <ScrollArea className="h-full border rounded-lg p-4 bg-white">
                      <div className="max-w-[210mm] mx-auto bg-white shadow-lg p-8" style={{
                        fontFamily: pdfSettings?.styling.fontFamily || 'Arial',
                        fontSize: `${pdfSettings?.styling.fontSize || 12}pt`,
                        color: pdfSettings?.styling.primaryColor || '#000000',
                        lineHeight: pdfSettings?.styling.lineSpacing || 1.5,
                      }}>
                        {/* Logo Preview */}
                        {pdfSettings?.logo && (
                          <div className={`mb-4 ${
                            pdfSettings.logo.position.includes('center') ? 'text-center' :
                            pdfSettings.logo.position.includes('right') ? 'text-right' : 'text-left'
                          }`}>
                            <img 
                              src={pdfSettings.logo.url} 
                              alt="Logo" 
                              style={{
                                width: `${pdfSettings.logo.width}px`,
                                height: `${pdfSettings.logo.height}px`,
                                display: 'inline-block'
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Header Preview */}
                        {pdfSettings?.header?.enabled && (
                          <div className="border-b pb-2 mb-4" style={{ height: `${pdfSettings.header.height}px` }}>
                            <div className="text-sm text-muted-foreground">{pdfSettings.header.content}</div>
                          </div>
                        )}

                        {/* Content Preview */}
                        {renderPreview()}

                        {/* Footer Preview */}
                        {pdfSettings?.footer?.enabled && (
                          <div className="border-t pt-2 mt-4" style={{ height: `${pdfSettings.footer.height}px` }}>
                            <div className="text-sm text-muted-foreground">{pdfSettings.footer.content}</div>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>

          {/* Right Panel - Conditional based on Output Type */}
          <div className="flex flex-col space-y-4 overflow-hidden">
            {formData.outputType === 'email' ? (
              // Email Settings Panel
              <ScrollArea className="flex-1">
                <div className="space-y-4 pr-4">
                  {/* Settings Card */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Template Settings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="is-default" className="text-sm">Set as Default</Label>
                        <Switch
                          id="is-default"
                          checked={formData.isDefault}
                          onCheckedChange={(checked) => setFormData({ ...formData, isDefault: checked })}
                        />
                      </div>

                      <Separator />

                      <div className="space-y-2">
                        <Label className="text-sm">Categories/Tags</Label>
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add category"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                          />
                          <Button size="sm" onClick={handleAddCategory}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {formData.category.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.category.map((cat) => (
                              <Badge key={cat} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveCategory(cat)}>
                                {cat}
                                <X className="h-3 w-3 ml-1" />
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Variables Card */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Available Variables</CardTitle>
                      <CardDescription className="text-xs">
                        Select a variable to insert into template
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Select onValueChange={(value) => handleInsertVariable(value)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a variable to insert..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                          {Object.entries(groupedVariables).map(([category, variables]) => (
                            <div key={category}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase bg-muted/50">
                                {category}
                              </div>
                              {variables.map((variable) => (
                                <SelectItem 
                                  key={variable.key} 
                                  value={variable.key}
                                  className="pl-4"
                                >
                                  <div className="flex flex-col py-1">
                                    <div className="font-mono text-xs text-blue-600">
                                      {variable.key}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {variable.label}
                                    </div>
                                    <div className="text-xs text-muted-foreground/70 italic">
                                      e.g., {variable.example}
                                    </div>
                                  </div>
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Variables will be inserted at the cursor position in the template content.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            ) : (
              // PDF Design Panel
              <PDFDesignInterface 
                pdfSettings={pdfSettings}
                onSettingsChange={setPdfSettings}
                onInsertVariable={handleInsertVariable}
              />
            )}
          </div>
        </div>
      </div>

      {/* Output Type Change Warning Dialog */}
      <AlertDialog open={showOutputTypeWarning} onOpenChange={setShowOutputTypeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Switch Output Type?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Switching from <strong>{formData.outputType === 'email' ? 'Email' : 'PDF'}</strong> to{' '}
              <strong>{pendingOutputType === 'email' ? 'Email' : 'PDF'}</strong> will change the available options.
              <br /><br />
              Your current content will be preserved, but format-specific settings may be lost. Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelOutputTypeChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOutputTypeChange}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave this page? All unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>Stay on Page</AlertDialogCancel>
            <AlertDialogAction onClick={confirmNavigation} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
